import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import {
  LayoutDashboard,
  Users,
  BookOpen,
  Settings,
  LogOut,
  Menu,
  X,
  Bell,
  Flame,
  ChevronRight,
  UsersRound,
  HelpCircle
} from "lucide-react";
import { Button } from "./ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import NotificationsDropdown from "./NotificationsDropdown";

const GBA_LOGO = "https://customer-assets.emergentagent.com/job_prospect-tracker-24/artifacts/aek653mu_GBA%20LOGO%20PNG.png";

const Layout = ({ children }) => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const navItems = [
    { path: "/", icon: LayoutDashboard, label: "Dashboard" },
    { path: "/prospects", icon: Users, label: "Prospects" },
    { path: "/techniques", icon: BookOpen, label: "32 Techniques" },
    { path: "/objections", icon: HelpCircle, label: "Objections" },
  ];

  if (user?.role === "admin") {
    navItems.push({ path: "/team", icon: UsersRound, label: "Team" });
    navItems.push({ path: "/admin", icon: Settings, label: "Admin" });
  }

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const isActive = (path) => location.pathname === path;

  return (
    <div className="min-h-screen bg-[#0B1120] flex flex-col">
      {/* Top Header - Mobile & Desktop */}
      <header className="sticky top-0 z-50 bg-[#3D4D6B]/95 backdrop-blur-md border-b border-slate-700/50">
        <div className="flex items-center justify-between px-4 py-3">
          <div className="flex items-center gap-3">
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="lg:hidden p-2 rounded-lg hover:bg-slate-700/50 text-slate-300"
              data-testid="mobile-menu-toggle"
            >
              {sidebarOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
            <Link to="/" className="flex items-center gap-3">
              <img src={GBA_LOGO} alt="GBA" className="h-10 w-auto" />
              <span className="hidden sm:block font-heading text-xl font-bold text-white tracking-wide">
                SALES ACCELERATION
              </span>
            </Link>
          </div>

          <div className="flex items-center gap-2">
            {/* Streak Badge */}
            <div className="hidden sm:flex items-center gap-1.5 bg-[#1E293B] px-3 py-1.5 rounded-full border border-slate-700">
              <Flame className="w-4 h-4 text-orange-400" />
              <span className="text-sm font-medium text-white">{user?.streak || 0}</span>
              <span className="text-xs text-slate-400">day streak</span>
            </div>

            {/* Notifications */}
            <NotificationsDropdown />

            {/* User Menu */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  className="flex items-center gap-2 hover:bg-slate-700/50"
                  data-testid="user-menu-trigger"
                >
                  <div className="w-8 h-8 rounded-full bg-gba-green flex items-center justify-center text-white font-bold">
                    {user?.name?.charAt(0).toUpperCase() || "U"}
                  </div>
                  <span className="hidden md:block text-sm text-white">{user?.name}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48 bg-[#1E293B] border-slate-700">
                <DropdownMenuItem className="text-slate-300">
                  <span className="text-xs text-slate-500">Signed in as</span>
                  <span className="block truncate">{user?.email}</span>
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={handleLogout}
                  className="text-red-400 hover:text-red-300 cursor-pointer"
                  data-testid="logout-button"
                >
                  <LogOut className="w-4 h-4 mr-2" />
                  Logout
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      <div className="flex flex-1">
        {/* Desktop Sidebar */}
        <aside className="hidden lg:flex flex-col w-60 bg-[#1E293B] border-r border-slate-700/50">
          <nav className="flex-1 p-4 space-y-2">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-all ${
                  isActive(item.path)
                    ? "bg-gba-green text-white shadow-glow"
                    : "text-slate-300 hover:bg-slate-700/50 hover:text-white"
                }`}
                data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, "-")}`}
              >
                <item.icon className="w-5 h-5" />
                <span className="font-medium">{item.label}</span>
                {isActive(item.path) && <ChevronRight className="w-4 h-4 ml-auto" />}
              </Link>
            ))}
          </nav>
        </aside>

        {/* Mobile Sidebar Overlay */}
        {sidebarOpen && (
          <div
            className="fixed inset-0 z-40 bg-black/50 lg:hidden"
            onClick={() => setSidebarOpen(false)}
          />
        )}

        {/* Mobile Sidebar */}
        <aside
          className={`fixed top-0 left-0 z-50 h-full w-64 bg-[#1E293B] transform transition-transform lg:hidden ${
            sidebarOpen ? "translate-x-0" : "-translate-x-full"
          }`}
        >
          <div className="flex items-center justify-between p-4 border-b border-slate-700">
            <img src={GBA_LOGO} alt="GBA" className="h-10 w-auto" />
            <button
              onClick={() => setSidebarOpen(false)}
              className="p-2 rounded-lg hover:bg-slate-700 text-slate-300"
            >
              <X size={20} />
            </button>
          </div>
          <nav className="p-4 space-y-2">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setSidebarOpen(false)}
                className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-all ${
                  isActive(item.path)
                    ? "bg-gba-green text-white"
                    : "text-slate-300 hover:bg-slate-700/50"
                }`}
              >
                <item.icon className="w-5 h-5" />
                <span className="font-medium">{item.label}</span>
              </Link>
            ))}
          </nav>
        </aside>

        {/* Main Content */}
        <main className="flex-1 overflow-auto pb-20 lg:pb-0">
          <div className="p-4 md:p-6 lg:p-8">{children}</div>
        </main>
      </div>

      {/* Mobile Bottom Navigation */}
      <nav className="fixed bottom-0 left-0 right-0 z-40 lg:hidden bg-[#3D4D6B] border-t border-slate-700/50 safe-area-inset-bottom">
        <div className="flex items-center justify-around py-2">
          {navItems.slice(0, 4).map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className={`flex flex-col items-center gap-1 px-4 py-2 rounded-lg transition-all ${
                isActive(item.path)
                  ? "text-gba-green"
                  : "text-slate-400 hover:text-slate-200"
              }`}
              data-testid={`mobile-nav-${item.label.toLowerCase().replace(/\s/g, "-")}`}
            >
              <item.icon className="w-5 h-5" />
              <span className="text-xs font-medium">{item.label.split(" ")[0]}</span>
            </Link>
          ))}
        </div>
      </nav>
    </div>
  );
};

export default Layout;
